import { Chart, Color } from 'chart.js';
import 'src/app/common/arrayUtils';
import { AggregationsTrend, toAggregationTrend } from '../models/api/aggregations.model';
import {
  AggregationTrendSeries,
  IChartData,
  PointInTime,
  SeriesTransformFunc,
  TypeSafeDataTransform,
  toDataTransform,
} from '../models/api/dashboards.model';
import { Severity } from '../models/api/governance/violationRule.model';
import { DetectionRuleTag } from '../models/api/finding.model';
import { ResourceCategory, ResourceStatus } from '../models/api/inventoryResource.model';
import { ViolationStatus } from '../models/api/violations.model';
import { ResourceType, Platform } from '../models/api';
import { ISelectedFilters } from '../models/filters/selectedFilters.interface';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { getResourceCategoryDisplayName } from './categoryUtils';

const color1 = '#00A6DA';
const color2 = '#0070F5';
const color3 = '#6E32E0';
const color4 = '#5F3DAC';
const color5 = '#402974';
const color6 = '#2A1A4A';
const yellow = '#f9cc00';
const orange = '#f98300';
const red = '#ef574c';
const green = '#46cc83';
const blue = '#1E90FF';
const purple = '#8A2BE2';
const pink = '#FF1493';
const turquoise = '#48D1CC';

export const pipe =
  (...transformFunctionList: SeriesTransformFunc[]) =>
  (series: PointInTime[]) =>
    transformFunctionList.reduce((acc, currentTransform) => currentTransform(acc), series);

export const dropLeadingZeros = (series: PointInTime[]) => series.dropWhile((point) => point.y === 0);

export const weeksInterval = (weeksInterval: number) => (series: PointInTime[]) =>
  series.filter((_, index) => index % (weeksInterval * 7) == 0);

export const daysInterval = (daysInterval: number) => (series: PointInTime[]) =>
  series.filter((_, index) => index % daysInterval == 0);

export enum Result {
  passed = 'passed',
  failed = 'failed',
}

export const violationsOvertimeCategories: TypeSafeDataTransform<ViolationStatus>[] = [
  { id: ViolationStatus.open, color: color1 },
  { id: ViolationStatus.resolved, color: color2 },
  { id: ViolationStatus.exempted, color: color4 },
].alsoForEach(
  (transform: TypeSafeDataTransform<ViolationStatus>) => (transform.seriesTransformationFunc = dropLeadingZeros)
);

export const resourceCategories: TypeSafeDataTransform<ResourceCategory>[] = [
  { id: ResourceCategory.application, color: color1 },
  { id: ResourceCategory.connection, color: color2 },
  { id: ResourceCategory.automation, color: color3 },
  { id: ResourceCategory.environment, color: color4 },
  { id: ResourceCategory.dataStorage, color: color5 },
  { id: ResourceCategory.bot, color: color6 },
];

export const orphandResourceCategories: TypeSafeDataTransform<ResourceCategory>[] = [
  { id: ResourceCategory.application, color: color1 },
  { id: ResourceCategory.connection, color: color2 },
  { id: ResourceCategory.automation, color: color3 },
  { id: ResourceCategory.environment, color: color4 },
  { id: ResourceCategory.dataStorage, color: color5 },
];

export const unusedResourceCategories: TypeSafeDataTransform<ResourceCategory>[] = [
  { id: ResourceCategory.automation, color: color3 },
  { id: ResourceCategory.environment, color: color4 },
];

export const builderCategories: TypeSafeDataTransform<ResourceCategory>[] = [
  { id: ResourceCategory.automation, color: color2 },
  { id: ResourceCategory.application, color: color3 },
  { id: ResourceCategory.dataStorage, color: color5 },
  { id: ResourceCategory.bot, color: color6 },
];

export const violationCategories: TypeSafeDataTransform<Severity>[] = [
  {
    id: Severity.high,
    color: red,
    pageLinkName: 'violations',
    selectedFilters: {
      severity: [Severity.high],
      violationStatus: [ViolationStatus.open],
      ruleTag: [DetectionRuleTag.generalAvailability],
    },
  },
  {
    id: Severity.medium,
    color: orange,
    pageLinkName: 'violations',
    selectedFilters: {
      severity: [Severity.medium],
      violationStatus: [ViolationStatus.open],
      ruleTag: [DetectionRuleTag.generalAvailability],
    },
  },
  {
    id: Severity.low,
    color: yellow,
    pageLinkName: 'violations',
    selectedFilters: {
      severity: [Severity.low],
      violationStatus: [ViolationStatus.open],
      ruleTag: [DetectionRuleTag.generalAvailability],
    },
  },
];

export const enterpriseAiViolationCategories: TypeSafeDataTransform<Severity>[] = [
  {
    id: Severity.high,
    color: red,
    pageLinkName: 'violations',
    selectedFilters: {
      severity: [Severity.high],
      violationStatus: [ViolationStatus.open],
      type: [ResourceType.conversational_plugin, ResourceType.connection_plugin, ResourceType.flow_plugin],
    },
  },
  {
    id: Severity.medium,
    color: orange,
    pageLinkName: 'violations',
    selectedFilters: {
      severity: [Severity.medium],
      violationStatus: [ViolationStatus.open],
      type: [ResourceType.conversational_plugin, ResourceType.connection_plugin, ResourceType.flow_plugin],
    },
  },
  {
    id: Severity.low,
    color: yellow,
    pageLinkName: 'violations',
    selectedFilters: {
      severity: [Severity.low],
      violationStatus: [ViolationStatus.open],
      type: [ResourceType.conversational_plugin, ResourceType.connection_plugin, ResourceType.flow_plugin],
    },
  },
];

export const m365copilotPluginTypes: TypeSafeDataTransform<ResourceType>[] = [
  {
    id: ResourceType.conversational_plugin,
    color: blue,
    pageLinkName: 'inventory',
    selectedFilters: { type: [ResourceType.conversational_plugin] },
  },
  {
    id: ResourceType.connection_plugin,
    color: green,
    pageLinkName: 'inventory',
    selectedFilters: { type: [ResourceType.connection_plugin] },
  },
  {
    id: ResourceType.flow_plugin,
    color: orange,
    pageLinkName: 'inventory',
    selectedFilters: { type: [ResourceType.flow_plugin] },
  },
];

export const copilot365AdoptionCategories: TypeSafeDataTransform<string>[] = [
  { id: 'users', color: orange },
  { id: 'interactions', color: purple },
];

export const owaspRiskCategoriesForAi: TypeSafeDataTransform<Severity>[] = [
  { id: 'Hard Coded Secrets', color: pink },
  { id: 'Vulnerability', color: green },
  { id: 'Sensitive Data', color: turquoise },
  { id: 'Impersonation', color: orange },
];

export const passedFailedCategories: TypeSafeDataTransform<Result>[] = [
  { id: Result.passed, color: green },
  { id: Result.failed, color: red },
];

export const platformCategories: TypeSafeDataTransform<Platform>[] = [
  {
    id: Platform.powerplatform,
    color: blue,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.powerplatform],
    },
  },
  {
    id: Platform.workato,
    color: orange,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.workato],
    },
  },
  {
    id: Platform.salesforce,
    color: green,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.salesforce],
    },
  },
  {
    id: Platform.powerBI,
    color: red,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.powerBI],
    },
  },
  {
    id: Platform.serviceNow,
    color: yellow,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.serviceNow],
    },
  },
  {
    id: Platform.chatGptEnterprise,
    color: purple,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.chatGptEnterprise],
    },
  },
  {
    id: Platform.amazonBedrock,
    color: turquoise,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.amazonBedrock],
    },
  },
  {
    id: Platform.google,
    color: pink,
    pageLinkName: 'inventory',
    selectedFilters: {
      category: [ResourceCategory.bot],
      resourceStatus: [ResourceStatus.active],
      platform: [Platform.google],
    },
  },
];

const toPercent = (series: PointInTime[]): PointInTime[] => {
  const start = series.findFirstBy(
    (item) => item.y,
    (value) => value !== 0,
    1
  );
  return series.alsoForEach((entry) => {
    entry.tooltip = entry.y.formatted();
    entry.y = Math.round((entry.y * 100) / start);
  });
};
export const lowCodeNoCodeAdoptionTransforms = resourceCategories.also((categories) =>
  categories.forEach((transform) => {
    transform.seriesTransformationFunc = pipe(dropLeadingZeros, toPercent, weeksInterval(1));
  })
);

export const copilotsAdoptionTransforms = copilot365AdoptionCategories.also((categories) =>
  categories.forEach((transform) => {
    transform.seriesTransformationFunc = pipe(dropLeadingZeros, daysInterval(1));
  })
);

export const provideChartData = <T>(
  dataTrend: AggregationsTrend,
  transform: TypeSafeDataTransform<T>[],
  labelSuffix = '',
  beautifyDisplayStringPipe?: BeautifyDisplayStringPipe
): AggregationTrendSeries[] =>
  transform
    .map(toDataTransform)
    .toMapBy((transform) => transform.id)
    .let((transformMap) =>
      Object.entries(dataTrend).map(
        ([valueAggregated, dataCountByTime]) =>
          ({
            name:
              Object.values(ResourceCategory).includes(valueAggregated as ResourceCategory) && beautifyDisplayStringPipe
                ? `${getResourceCategoryDisplayName(valueAggregated, beautifyDisplayStringPipe)}${labelSuffix}`
                : `${valueAggregated}${labelSuffix}`,
            data: dataCountByTime.map(toAggregationTrend),
            transform: transformMap.get(valueAggregated),
          }) as AggregationTrendSeries
      )
    );

export const createChartData = <T, RAW>(
  dataDict: Record<string, RAW>,
  transform: TypeSafeDataTransform<T>[],
  dataAdapter: (raw: RAW) => number = (raw) => raw as number
) =>
  transform
    .map(toDataTransform)
    .map((transform) =>
      createSingleChartData(
        transform.id,
        dataDict[transform.id]?.let(dataAdapter) ?? 0,
        transform.color,
        transform.pageLinkName,
        transform.selectedFilters
      )
    );

export function createSingleChartData<T>(
  category: string,
  value: T,
  color: Color,
  pageLinkName?: string,
  selectedFilters?: ISelectedFilters
): IChartData {
  // Capitalize first letter of category for display
  const displayName = category.charAt(0).toUpperCase() + category.slice(1);
  return {
    name: displayName,
    value: value,
    itemStyle: { color: color },
    pageLinkName,
    selectedFilters,
  } as IChartData;
}

export function chartOnClickBehavior(chart: Chart, chosenDatasetIndex: number) {
  const hiddenDatasetsCount = chart.data.datasets.filter((e, datasetIndex) =>
    isDatasetHidden(chart, datasetIndex)
  ).length;

  // We change the visibility of the datasets by the following logic:
  // 1. We check whether there aren't any hidden datasets yet.
  //      1.1. If there aren't any hidden datasets yet, then only the chosen dataset should remain selected and all the rest should become hidden.
  //      1.2. If there are any hidden datasets already, then the chosen dataset should revert its hidden state.
  //          1.2.1 If the chosen dataset was the only visible dataset and it was clicked to become hidden, make all the datasets visible.
  if (hiddenDatasetsCount === 0) {
    chart.data.datasets.forEach((_, index) =>
      index != chosenDatasetIndex ? (chart.getDatasetMeta(index).hidden = true) : ''
    );
  } else {
    const currentHiddenStateForChosenDataset = chart.getDatasetMeta(chosenDatasetIndex).hidden;

    if (!currentHiddenStateForChosenDataset && hiddenDatasetsCount === chart.data.datasets.length - 1) {
      chart.data.datasets.forEach((e, datasetIndex) => {
        const datasetMeta = chart.getDatasetMeta(datasetIndex);
        if (datasetIndex !== chosenDatasetIndex) {
          datasetMeta.hidden = false;
        }
      });
    } else {
      chart.getDatasetMeta(chosenDatasetIndex).hidden = !currentHiddenStateForChosenDataset;
    }
  }

  chart.update();
}

export function setCursorStyle(chartEventAsHtmlElement: HTMLElement, cursorStyle: string): void {
  chartEventAsHtmlElement && (chartEventAsHtmlElement.style.cursor = cursorStyle);
}

function isDatasetHidden(chart: Chart, datasetIndex: number | undefined): boolean {
  return datasetIndex === undefined || chart.getDatasetMeta(datasetIndex).hidden === null
    ? false
    : chart.getDatasetMeta(datasetIndex).hidden;
}
